/**
 * On-page confirm / alert / copy boxes in place of the browser's window.confirm, alert and prompt
 * (Joe, 2026-11-04: "switch all browser boxes"). One <DialogHost /> is mounted at the app root;
 * confirmBox() and friends return a promise like the browser calls did, so call sites stay simple:
 *
 *   if (!(await confirmBox("Delete this photo?"))) return;
 */
import { useEffect, useRef, useState } from "react";

interface Request {
  title: string;
  message: string;
  ok: string;
  cancel: string | null;
  danger: boolean;
  copy?: string;
  resolve: (ok: boolean) => void;
}

let show: ((r: Request) => void) | null = null;
const waiting: Request[] = [];

function open(r: Omit<Request, "resolve">): Promise<boolean> {
  return new Promise((resolve) => {
    const req = { ...r, resolve };
    if (show) show(req); else waiting.push(req);
  });
}

export interface ConfirmOptions { title?: string; ok?: string; cancel?: string; danger?: boolean }

/** Asks a yes/no question on the page. Resolves true for the OK button, false for Cancel, Escape or ×. */
export const confirmBox = (message: string, o: ConfirmOptions = {}) =>
  open({ title: o.title ?? "Please confirm", message, ok: o.ok ?? "OK", cancel: o.cancel ?? "Cancel", danger: o.danger ?? false });

/** Shows a message with one OK button. */
export const alertBox = (message: string, title = "Something went wrong") =>
  open({ title, message, ok: "OK", cancel: null, danger: false }).then(() => undefined);

/** Shows text selected and ready to copy (used when the clipboard is not available). */
export const copyBox = (message: string, copy: string) =>
  open({ title: "Copy", message, ok: "Done", cancel: null, danger: false, copy }).then(() => undefined);

/** Mount once near the root of the app. */
export function DialogHost() {
  const [queue, setQueue] = useState<Request[]>([]);
  useEffect(() => {
    show = (r) => setQueue((q) => [...q, r]);
    if (waiting.length) setQueue((q) => [...q, ...waiting.splice(0)]);
    return () => { show = null; };
  }, []);
  const current = queue[0];
  if (!current) return null;
  const done = (ok: boolean) => { current.resolve(ok); setQueue((q) => q.slice(1)); };
  return <Box key={queue.length + current.message} req={current} done={done} />;
}

function Box({ req, done }: { req: Request; done: (ok: boolean) => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const okRef = useRef<HTMLButtonElement>(null);
  const copyRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    if (copyRef.current) copyRef.current.select(); else okRef.current?.focus();
    return () => d?.close();
  }, []);
  return (
    <dialog ref={ref} className="modal confirm-box" aria-label={req.title}
      onCancel={(e) => { e.preventDefault(); done(false); }}>
      <header><h3>{req.title}</h3></header>
      <div className="modal-body">
        <p className="confirm-text">{req.message}</p>
        {req.copy !== undefined && <input ref={copyRef} className="confirm-copy" readOnly value={req.copy} onFocus={(e) => e.target.select()} />}
        <div className="confirm-buttons">
          {req.cancel && <button type="button" className="btn outline" onClick={() => done(false)}>{req.cancel}</button>}
          <button type="button" ref={okRef} className={req.danger ? "btn danger" : "btn primary"} onClick={() => done(true)}>{req.ok}</button>
        </div>
      </div>
    </dialog>
  );
}
